import React, { useState, useEffect } from 'react';
import { getProfile } from '../../api/services/user/getProfile';
import { updateProfile } from '../../api/services/user/updateProfile';
import { updateAddress } from '../../api/services/user/updateAddress';
import { changePassword } from '../../api/services/user/changePassword';
import Header from '../Common Utility/Header';
import Footer from '../Common Utility/Footer';
import Loader from '../Common Utility/Loader';
import './Profile.css';

const Profile = () => {
    const [profile, setProfile] = useState({
        givenName: '',
        surname: '',
        mail: '',
        address: {
            streetAddress: '',
            city: '',
            country: '',
            postalCode: ''
        }
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [address, setAddress] = useState({
        streetAddress: '',
        city: '',
        country: '',
        postalCode: ''
    });

    useEffect(() => {
        const fetchProfile = async () => {
            setLoading(true);
            try {
                const response = await getProfile();
                setProfile({
                    ...response,
                    address: response.address || {
                        streetAddress: '',
                        city: '',
                        country: '',
                        postalCode: ''
                    }
                });
                setAddress(response.address || {
                    streetAddress: '',
                    city: '',
                    country: '',
                    postalCode: ''
                });
                setError(null);
            } catch (error) {
                console.error('Error fetching profile:', error);
                setError('Failed to load profile. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfile((prevProfile) => ({ ...prevProfile, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswords((prevPasswords) => ({ ...prevPasswords, [name]: value }));
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setAddress((prevAddress) => ({ ...prevAddress, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await updateProfile({
                givenName: profile.givenName,
                surname: profile.surname,
            });
            setIsEditing(false);
            setError(null);
        } catch (error) {
            console.error('Error updating profile:', error);
            setError('Failed to update profile. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddressSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await updateAddress(address);
            setProfile((prev) => ({
                ...prev,
                address: { ...address }
            }));
            setIsEditingAddress(false);
            setError(null);
        } catch (error) {
            console.error('Error updating address:', error);
            setError('Failed to update address. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (passwords.newPassword !== passwords.confirmPassword) {
            setError('New password and confirm password do not match.');
            return;
        }
        setLoading(true);
        try {
            await changePassword({
                currentPassword: passwords.currentPassword,
                newPassword: passwords.newPassword,
            });
            setIsChangingPassword(false);
            setError(null);
        } catch (error) {
            console.error('Error changing password:', error);
            setError('Failed to change password. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div>
                <Header />
                <div className="profile-container">
                    <Loader />
                </div>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <Header />
                <div className="profile-container">
                    <p style={{ color: 'red' }}>{error}</p>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div>
            <div className="profile-container">
                {isEditing ? (
                    <div className="profile-card">
                        <h2>Edit Profile</h2>
                        <form onSubmit={handleSubmit}>
                            <input
                                type="text"
                                name="givenName"
                                value={profile.givenName}
                                onChange={handleChange}
                                placeholder="Given Name"
                            />
                            <input
                                type="text"
                                name="surname"
                                value={profile.surname}
                                onChange={handleChange}
                                placeholder="Surname"
                            />
                            <button type="submit">Save</button>
                            <button type="button" onClick={() => setIsEditing(false)}>Cancel</button>
                        </form>
                    </div>
                ) : isChangingPassword ? (
                    <div className="profile-card">
                        <h2>Change Password</h2>
                        <form onSubmit={handlePasswordSubmit}>
                            <input
                                type="password"
                                name="currentPassword"
                                value={passwords.currentPassword}
                                onChange={handlePasswordChange}
                                placeholder="Current Password"
                            />
                            <input
                                type="password"
                                name="newPassword"
                                value={passwords.newPassword}
                                onChange={handlePasswordChange}
                                placeholder="New Password"
                            />
                            <input
                                type="password"
                                name="confirmPassword"
                                value={passwords.confirmPassword}
                                onChange={handlePasswordChange}
                                placeholder="Confirm Password"
                            />
                            <button type="submit">Change Password</button>
                            <button type="button" onClick={() => setIsChangingPassword(false)}>Cancel</button>
                        </form>
                    </div>
                ) : isEditingAddress ? (
                    <div className="profile-card">
                        <h2>Edit Address</h2>
                        <form onSubmit={handleAddressSubmit}>
                            <input
                                type="text"
                                name="streetAddress"
                                value={address.streetAddress}
                                onChange={handleAddressChange}
                                placeholder="Street Address"
                            />
                            <input
                                type="text"
                                name="city"
                                value={address.city}
                                onChange={handleAddressChange}
                                placeholder="City"
                            />
                            <input
                                type="text"
                                name="country"
                                value={address.country}
                                onChange={handleAddressChange}
                                placeholder="Country"
                            />
                            <input
                                type="text"
                                name="postalCode"
                                value={address.postalCode}
                                onChange={handleAddressChange}
                                placeholder="Postal Code"
                            />
                            <button type="submit">Save Address</button>
                            <button type="button" onClick={() => setIsEditingAddress(false)}>Cancel</button>
                        </form>
                    </div>
                ) : (
                    <div className="profile-card">
                        <h2>Hi, {profile.mail}</h2>
                        <h3>{profile.givenName} {profile.surname}</h3>
                        {/* <div style={{ marginBottom: '1rem' }}>
                            <strong>Address:</strong>
                            <div>{profile.address?.streetAddress}</div>
                            <div>{profile.address?.city}</div>
                            <div>{profile.address?.country}</div>
                            <div>{profile.address?.postalCode}</div>
                        </div> */}
                        <button onClick={() => setIsEditing(true)}>Edit Profile</button>
                        <button onClick={() => setIsEditingAddress(true)}>Edit Address</button>
                        <button onClick={() => setIsChangingPassword(true)}>Change Password</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Profile;